/**
 * Dashboard — account overview.
 *
 * One request, GET /brokers/dashboard-summary, returns a real aggregate: funds, live-priced positions and
 * holdings, the local order book (risk and broker rejections included), the risk engine's guardrails,
 * strategy state, watchlist movers and a per-day net-value history that starts the first day this page
 * is opened. Prices for positions and movers then stream over the shared market websocket. Nothing here is
 * sample data — a section whose source is unavailable says so.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  BarChart3,
  LayoutDashboard,
  ListOrdered,
  Plug,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Wallet,
  Zap,
} from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api, getErrorMessage } from '@/config/api';
import { CHART, CHART_AXIS, CHART_TOOLTIP } from '@/lib/chart';
import {
  formatINR,
  formatINRCompact,
  formatPctFixed,
  formatPrice,
  formatQty,
  formatSignedINR,
  formatTime,
  signClass,
  signOf,
  toNum,
} from '@/lib/format';
import { cn } from '@/lib/cn';
import { useMarketWebSocket } from '@/hooks/use-websocket';
import { useMarketStore } from '@/stores/market-store';
import type { Tone } from '@/components/ui';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
} from '@/components/ui';

// ── Types (mirror api/schemas/dashboard.py; numerics arrive as strings) ─────

interface Funds {
  available_cash: string;
  available_margin: string;
  used_margin: string;
  total_collateral: string;
  mtm: string;
}
interface Metrics {
  net_value: string;
  funds_total: string;
  today_pnl: string;
  holdings_value: string;
  holdings_invested: string;
  holdings_pnl: string;
  holdings_pnl_pct: string;
  holdings_day_pnl: string;
  positions_unrealized_pnl: string;
  positions_realized_pnl: string;
  positions_day_pnl: string;
  open_positions: number;
  holdings_count: number;
}
interface Position {
  trading_symbol: string;
  exchange_segment: string;
  instrument_token: string;
  product: string;
  net_quantity: number;
  average_price: string;
  ltp: string;
  unrealized_pnl: string;
  day_pnl: string;
  lot_size: number;
}
interface Allocation {
  trading_symbol: string;
  value: string;
  weight_pct: string;
  pnl: string;
}
interface OrdersToday {
  total: number;
  filled: number;
  pending: number;
  rejected: number;
  cancelled: number;
}
interface RecentOrder {
  id: string;
  broker_order_id: string;
  trading_symbol: string;
  exchange_segment: string;
  transaction_type: string;
  order_type: string;
  product: string;
  quantity: number;
  filled_quantity: number;
  price: string;
  average_price: string;
  status: string;
  rejection_reason: string;
  strategy_name: string;
  tag: string;
  created_at: string | null;
  filled_at: string | null;
}
interface Mover {
  instrument_token: string;
  trading_symbol: string;
  exchange_segment: string;
  ltp: string;
  change: string;
  change_pct: string;
}
interface Risk {
  kill_switch_active: boolean;
  circuit_breaker_active: boolean;
  daily_realized_pnl: string;
  max_daily_loss: string;
  loss_used_pct: string;
  orders_placed: number;
  max_orders_per_day: number;
  max_exposure: string;
  used_margin: string;
}
interface Signal {
  strategy_name: string;
  signal_type: string;
  trading_symbol: string;
  suggested_price: string;
  timestamp: string | null;
}
interface Strategies {
  total: number;
  active: number;
  paused: number;
  auto_execute: number;
  recent_signals: Signal[];
}
interface DashboardSummary {
  is_connected: boolean;
  connection_display_name: string;
  connection_ucc: string;
  as_of: string;
  funds: Funds | null;
  metrics: Metrics | null;
  positions: Position[];
  holdings_allocation: Allocation[];
  orders_today: OrdersToday;
  recent_orders: RecentOrder[];
  watchlist_movers: { gainers: Mover[]; losers: Mover[]; tracked: number };
  risk: Risk | null;
  strategies: Strategies | null;
  equity: { date: string; value: string }[];
  warnings: string[];
}

const POLL_MS = 20_000;

const ORDER_TONE: Record<string, Tone> = {
  filled: 'success',
  partial: 'info',
  accepted: 'info',
  sent: 'info',
  created: 'neutral',
  modified: 'info',
  trigger_pending: 'warning',
  rejected: 'danger',
  cancelled: 'neutral',
  expired: 'neutral',
};

function sideLabel(side: string): { label: string; tone: Tone } {
  const s = (side || '').toUpperCase();
  return s === 'B' || s === 'BUY' ? { label: 'BUY', tone: 'success' } : { label: 'SELL', tone: 'danger' };
}

function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ticks = useMarketStore((s) => s.ticks);
  const { isConnected: socketUp, subscribe, unsubscribe } = useMarketWebSocket();

  // Initial load + poll. The poll skips the broker-book sync (the Orders page and the fills feed keep
  // statuses current); a manual refresh forces it.
  useEffect(() => {
    let cancelled = false;
    const load = async (sync: boolean) => {
      if (document.visibilityState === 'hidden') return;
      try {
        const { data: payload } = await api.get<DashboardSummary>('/brokers/dashboard-summary', {
          params: { sync },
        });
        if (cancelled) return;
        setData(payload);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: payload } = await api.get<DashboardSummary>('/brokers/dashboard-summary', {
        params: { sync: true },
      });
      setData(payload);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setRefreshing(false);
    }
  };

  // Live overlay: subscribe the tokens on screen and apply tick deltas on top of the snapshot.
  const liveTokens = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set<string>();
    for (const p of data.positions) if (p.instrument_token) set.add(p.instrument_token);
    for (const m of [...data.watchlist_movers.gainers, ...data.watchlist_movers.losers]) set.add(m.instrument_token);
    return [...set];
  }, [data]);

  useEffect(() => {
    if (!socketUp || liveTokens.length === 0) return;
    subscribe(liveTokens);
    return () => unsubscribe(liveTokens);
  }, [socketUp, liveTokens, subscribe, unsubscribe]);

  const connected = Boolean(data?.is_connected);
  const metrics = data?.metrics ?? null;
  const funds = data?.funds ?? null;
  const ordersToday = data?.orders_today ?? { total: 0, filled: 0, pending: 0, rejected: 0, cancelled: 0 };

  const livePositions = (data?.positions ?? []).map((p) => {
    const tick = ticks[p.instrument_token];
    const liveLtp = tick ? toNum(tick.ltp, 0) : 0;
    const snapLtp = toNum(p.ltp, 0);
    if (liveLtp <= 0 || snapLtp <= 0) return { ...p, ltpNum: snapLtp, day: toNum(p.day_pnl), overall: toNum(p.unrealized_pnl) };
    const delta = (liveLtp - snapLtp) * p.net_quantity;
    return { ...p, ltpNum: liveLtp, day: toNum(p.day_pnl) + delta, overall: toNum(p.unrealized_pnl) + delta };
  });
  const livePositionsDelta = livePositions.reduce((acc, p) => acc + (p.day - toNum(p.day_pnl)), 0);
  const todayPnl = metrics ? toNum(metrics.today_pnl) + livePositionsDelta : 0;

  const liveMover = (m: Mover) => {
    const tick = ticks[m.instrument_token];
    if (tick && toNum(tick.ltp, 0) > 0) {
      return { ...m, ltp: tick.ltp, change: tick.change, change_pct: tick.change_percent };
    }
    return m;
  };

  const usedMargin = toNum(funds?.used_margin);
  const availableMargin = toNum(funds?.available_margin);
  const marginTotal = usedMargin + availableMargin;
  const usedPct = marginTotal > 0 ? Math.round((usedMargin / marginTotal) * 100) : 0;

  const risk = data?.risk ?? null;
  const lossUsedPct = Math.min(100, Math.max(0, toNum(risk?.loss_used_pct)));
  const strategies = data?.strategies ?? null;
  const equity = data?.equity ?? [];
  const allocation = data?.holdings_allocation ?? [];

  const container = {
    hidden: { opacity: reduceMotion ? 1 : 0 },
    show: { opacity: 1, transition: { staggerChildren: reduceMotion ? 0 : 0.04 } },
  };
  const item = {
    hidden: { opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 6 },
    show: { opacity: 1, y: 0, transition: { duration: 0.18 } },
  };

  const linkButton = (label: string, to: string) => (
    <Button variant="ghost" size="sm" icon={ArrowRight} onClick={() => navigate(to)}>
      {label}
    </Button>
  );

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="flex flex-col gap-6">
      <PageHeader
        icon={LayoutDashboard}
        title="Overview"
        description="Your account at a glance — funds, P&L, positions, orders, guardrails and automation."
        meta={
          <span className="flex flex-wrap items-center gap-2">
            {data && (
              <Badge tone={connected ? 'success' : 'warning'} icon={connected ? ShieldCheck : Plug}>
                {connected ? `${data.connection_display_name || 'Kotak Neo'} connected` : 'No broker session'}
              </Badge>
            )}
            {data && (
              <span className="text-xs text-fg-subtle">
                Updated {formatTime(data.as_of)} · refreshes every {POLL_MS / 1000}s
              </span>
            )}
          </span>
        }
        actions={
          <Button variant="secondary" size="sm" icon={RefreshCw} loading={refreshing} onClick={refresh}>
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert
          tone="danger"
          title="Couldn't load your dashboard"
          action={
            <Button variant="secondary" size="sm" onClick={refresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {data && !connected && !loading && (
        <EmptyState
          icon={Plug}
          title="No broker connected"
          description="Connect your Kotak Neo account to see funds, positions, holdings and live P&L here."
          action={
            <Button variant="primary" size="sm" icon={Plug} onClick={() => navigate('/settings')}>
              Connect broker
            </Button>
          }
        />
      )}

      {data && data.warnings.length > 0 && (
        <Alert tone="warning" title="Some sections are incomplete">
          <ul className="list-disc pl-4">
            {data.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* KPIs */}
      <motion.div variants={item} className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Account value"
          value={formatINR(metrics ? toNum(metrics.net_value) + livePositionsDelta : undefined)}
          loading={loading}
          icon={Wallet}
          hint={
            metrics
              ? `Funds ${formatINRCompact(metrics.funds_total)} · Holdings ${formatINRCompact(metrics.holdings_value)} · Open P&L ${formatSignedINR(toNum(metrics.positions_unrealized_pnl) + livePositionsDelta)}`
              : 'Funds + delivery holdings + open position P&L'
          }
        />
        <StatCard
          label="Today's P&L"
          value={<span className={signClass(todayPnl)}>{metrics ? formatSignedINR(todayPnl) : '—'}</span>}
          loading={loading}
          icon={Activity}
          hint={
            metrics
              ? `Positions ${formatSignedINR(toNum(metrics.positions_day_pnl) + livePositionsDelta)} · Holdings ${formatSignedINR(metrics.holdings_day_pnl)}`
              : 'Positions MTM + holdings day change'
          }
        />
        <StatCard
          label="Holdings P&L"
          value={<span className={signClass(metrics?.holdings_pnl)}>{formatSignedINR(metrics?.holdings_pnl)}</span>}
          loading={loading}
          icon={BarChart3}
          delta={
            metrics && toNum(metrics.holdings_invested) > 0
              ? { value: formatPctFixed(metrics.holdings_pnl_pct), direction: signOf(metrics.holdings_pnl) }
              : undefined
          }
          hint={metrics ? `Invested ${formatINRCompact(metrics.holdings_invested)} · ${metrics.holdings_count} holdings` : '—'}
        />
        <StatCard
          label="Available margin"
          value={formatINR(funds?.available_margin)}
          loading={loading}
          icon={Wallet}
          hint={
            <span className="flex w-full flex-col gap-1.5">
              <span className="text-fg-faint">
                {formatINRCompact(funds?.used_margin)} used · {usedPct}%
              </span>
              <span className="block h-1 w-full overflow-hidden rounded-full bg-surface-3">
                <span
                  className="block h-full rounded-full bg-brand transition-[width] duration-300"
                  style={{ width: `${Math.min(usedPct, 100)}%` }}
                />
              </span>
            </span>
          }
        />
        <StatCard
          label="Orders today"
          value={formatQty(ordersToday.total, '0')}
          loading={loading}
          icon={ListOrdered}
          hint={`${ordersToday.filled} filled · ${ordersToday.pending} pending · ${ordersToday.rejected} rejected`}
        />
      </motion.div>

      {/* Positions + guardrails */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <motion.div variants={item} className="xl:col-span-2">
          <Card className="h-full">
            <CardHeader
              title="Open positions"
              description={
                metrics
                  ? `${metrics.open_positions} open · unrealized ${formatSignedINR(toNum(metrics.positions_unrealized_pnl) + livePositionsDelta)} · realized today ${formatSignedINR(metrics.positions_realized_pnl)}`
                  : 'Today’s MTM and overall P&L per leg'
              }
              actions={linkButton('Holdings & Positions', '/portfolio')}
            />
            {loading ? (
              <CardBody>
                <Skeleton className="h-32 w-full" />
              </CardBody>
            ) : livePositions.length === 0 ? (
              <EmptyState
                icon={Activity}
                title="No open positions"
                description={connected ? 'Anything you buy or sell today shows up here with live P&L.' : 'Connect your broker to see positions.'}
                className="py-10"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-fg-subtle">
                      <th className="px-5 py-2 font-medium">Instrument</th>
                      <th className="px-3 py-2 text-right font-medium">Qty</th>
                      <th className="px-3 py-2 text-right font-medium">Avg</th>
                      <th className="px-3 py-2 text-right font-medium">LTP</th>
                      <th className="px-3 py-2 text-right font-medium">Today</th>
                      <th className="px-5 py-2 text-right font-medium">Overall</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {livePositions.map((p) => (
                      <tr key={`${p.exchange_segment}:${p.instrument_token}:${p.product}`}>
                        <td className="px-5 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-fg">{p.trading_symbol}</span>
                            <Badge tone="neutral">{p.product}</Badge>
                          </div>
                          <div className="text-[11px] text-fg-faint uppercase">{p.exchange_segment.replace('_', ' ')}</div>
                        </td>
                        <td className={cn('px-3 py-2.5 text-right font-tabular', p.net_quantity < 0 ? 'text-loss' : 'text-fg')}>
                          {p.net_quantity > 0 ? '+' : ''}
                          {p.net_quantity}
                        </td>
                        <td className="px-3 py-2.5 text-right font-tabular text-fg-muted">{formatPrice(p.average_price)}</td>
                        <td className="px-3 py-2.5 text-right font-tabular text-fg">{formatPrice(p.ltpNum)}</td>
                        <td className={cn('px-3 py-2.5 text-right font-tabular', signClass(p.day))}>{formatSignedINR(p.day)}</td>
                        <td className={cn('px-5 py-2.5 text-right font-tabular', signClass(p.overall))}>{formatSignedINR(p.overall)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </motion.div>

        <motion.div variants={item} className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Risk guardrails" description="What the risk engine enforces today" actions={linkButton('Risk Engine', '/risk')} />
            <CardBody className="flex flex-col gap-4">
              {!risk ? (
                <Skeleton className="h-20 w-full" />
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    <Badge tone={risk.kill_switch_active ? 'danger' : 'success'} icon={risk.kill_switch_active ? ShieldAlert : ShieldCheck}>
                      {risk.kill_switch_active ? 'Kill switch ON — trading halted' : 'Kill switch off'}
                    </Badge>
                    <Badge tone={risk.circuit_breaker_active ? 'warning' : 'neutral'}>
                      {risk.circuit_breaker_active ? 'Circuit breaker tripped' : 'Circuit breaker armed'}
                    </Badge>
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between text-xs">
                      <span className="text-fg-subtle">Daily loss used</span>
                      <span className="font-tabular text-fg-muted">
                        {formatINR(Math.max(0, -toNum(risk.daily_realized_pnl)))} of {formatINR(risk.max_daily_loss)}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                      <div
                        className={cn('h-full rounded-full transition-[width] duration-300', lossUsedPct >= 80 ? 'bg-loss' : lossUsedPct >= 50 ? 'bg-warning' : 'bg-brand')}
                        style={{ width: `${lossUsedPct}%` }}
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-fg-faint">
                      Realized today {formatSignedINR(risk.daily_realized_pnl)} · {formatPctFixed(risk.loss_used_pct)} of the limit
                    </p>
                  </div>
                  <dl className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <dt className="text-fg-subtle">Orders today</dt>
                      <dd className="font-tabular text-fg">
                        {risk.orders_placed} / {risk.max_orders_per_day || '∞'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-fg-subtle">Margin in use</dt>
                      <dd className="font-tabular text-fg">{formatINRCompact(risk.used_margin)}</dd>
                    </div>
                  </dl>
                </>
              )}
            </CardBody>
          </Card>

          <Card className="flex-1">
            <CardHeader title="Automation" description="Strategies and their latest signals" actions={linkButton('Strategies', '/strategies')} />
            <CardBody className="flex flex-col gap-3">
              {!strategies ? (
                <Skeleton className="h-16 w-full" />
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    <Badge tone={strategies.active > 0 ? 'success' : 'neutral'} icon={Zap}>
                      {strategies.active} active
                    </Badge>
                    {strategies.paused > 0 && <Badge tone="warning">{strategies.paused} paused</Badge>}
                    <Badge tone={strategies.auto_execute > 0 ? 'brand' : 'neutral'}>{strategies.auto_execute} auto-trading</Badge>
                    <span className="self-center text-xs text-fg-faint">{strategies.total} total</span>
                  </div>
                  {strategies.recent_signals.length === 0 ? (
                    <p className="text-xs text-fg-subtle">No signals yet. Activate a strategy and pin instruments to it.</p>
                  ) : (
                    <ul className="divide-y divide-line text-xs">
                      {strategies.recent_signals.map((s, i) => {
                        const side = sideLabel(s.signal_type);
                        return (
                          <li key={`${s.timestamp}-${i}`} className="flex items-center justify-between gap-2 py-1.5">
                            <span className="min-w-0 truncate text-fg-muted">
                              <span className="text-fg">{s.strategy_name}</span> · {side.label} {s.trading_symbol}
                            </span>
                            <span className="shrink-0 font-tabular text-fg-subtle">
                              {formatPrice(s.suggested_price)} · {formatTime(s.timestamp)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </motion.div>
      </div>

      {/* Orders + movers */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <motion.div variants={item} className="xl:col-span-2">
          <Card className="h-full">
            <CardHeader
              title="Recent orders"
              description="Everything placed through the platform, including risk and broker rejections"
              actions={linkButton('Order book', '/orders')}
            />
            {loading ? (
              <CardBody>
                <Skeleton className="h-32 w-full" />
              </CardBody>
            ) : (data?.recent_orders.length ?? 0) === 0 ? (
              <EmptyState icon={ListOrdered} title="No orders yet" description="Orders you place show up here with their broker status." className="py-10" />
            ) : (
              <ul className="divide-y divide-line">
                {data!.recent_orders.map((o) => {
                  const side = sideLabel(o.transaction_type);
                  const filled = o.status === 'filled' || o.status === 'partial';
                  const px = filled && toNum(o.average_price) > 0 ? o.average_price : o.price;
                  return (
                    <li key={o.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-fg">{o.trading_symbol}</span>
                          <Badge tone={side.tone}>{side.label}</Badge>
                          <span className="text-[11px] text-fg-faint uppercase">
                            {o.product} · {o.order_type}
                            {o.strategy_name ? ` · ${o.strategy_name}` : ''}
                          </span>
                        </div>
                        <div className="mt-0.5 font-tabular text-xs text-fg-subtle">
                          {filled ? `${o.filled_quantity}/${o.quantity}` : o.quantity} @ {toNum(px) > 0 ? formatINR(px) : 'MKT'}
                          {o.broker_order_id ? ` · #${o.broker_order_id}` : ''}
                        </div>
                        {o.status === 'rejected' && o.rejection_reason && (
                          <p className="mt-0.5 truncate text-xs text-loss" title={o.rejection_reason}>
                            {o.rejection_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge tone={ORDER_TONE[o.status] ?? 'neutral'}>{o.status.replace('_', ' ')}</Badge>
                        <span className="font-tabular text-xs text-fg-subtle">{formatTime(o.filled_at ?? o.created_at)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="h-full">
            <CardHeader
              title="Watchlist movers"
              description={data ? `${data.watchlist_movers.tracked} instruments tracked` : 'Top gainers and losers on your watchlist'}
              actions={linkButton('Market Watch', '/market')}
            />
            {loading ? (
              <CardBody>
                <Skeleton className="h-32 w-full" />
              </CardBody>
            ) : (data?.watchlist_movers.tracked ?? 0) === 0 ? (
              <EmptyState
                icon={Activity}
                title="Watchlist is empty"
                description="Add instruments on Market Watch and the biggest movers appear here."
                className="py-10"
              />
            ) : (
              <CardBody className="flex flex-col gap-4">
                {(['gainers', 'losers'] as const).map((group) => {
                  const rows = (data?.watchlist_movers[group] ?? []).map(liveMover);
                  return (
                    <div key={group}>
                      <p className="mb-1.5 text-[11px] font-medium tracking-wider text-fg-faint uppercase">{group}</p>
                      {rows.length === 0 ? (
                        <p className="text-xs text-fg-subtle">None right now.</p>
                      ) : (
                        <ul className="divide-y divide-line">
                          {rows.map((m) => (
                            <li key={m.instrument_token} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                              <span className="min-w-0 truncate font-medium text-fg">{m.trading_symbol}</span>
                              <span className="flex shrink-0 items-baseline gap-2 font-tabular">
                                <span className="text-fg-muted">{formatPrice(m.ltp)}</span>
                                <span className={cn('w-20 text-right text-xs', signClass(m.change_pct))}>{formatPctFixed(m.change_pct)}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </CardBody>
            )}
          </Card>
        </motion.div>
      </div>

      {/* History + allocation */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <motion.div variants={item} className="xl:col-span-2">
          <Card className="h-full">
            <CardHeader
              title="Net value history"
              description="One point per trading day, recorded whenever this page loads — no back-filled history"
            />
            <CardBody>
              {equity.length < 2 ? (
                <EmptyState
                  icon={BarChart3}
                  title={equity.length === 1 ? 'Tracking started today' : 'No history yet'}
                  description={
                    equity.length === 1
                      ? `Today's net value ${formatINR(equity[0].value)} is saved. The curve fills in one point per day from here.`
                      : 'Connect your broker; the first snapshot is taken as soon as funds are available.'
                  }
                  className="py-10"
                />
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={equity.map((p) => ({ date: shortDate(p.date), value: toNum(p.value) }))}>
                      <defs>
                        <linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CHART.brand} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={CHART.brand} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="date" {...CHART_AXIS} />
                      <YAxis {...CHART_AXIS} width={64} tickFormatter={(v: number) => formatINRCompact(v)} domain={['auto', 'auto']} />
                      <Tooltip
                        contentStyle={CHART_TOOLTIP}
                        formatter={(value) => [formatINR(toNum(value as string | number | undefined)), 'Net value']}
                      />
                      <Area type="monotone" dataKey="value" stroke={CHART.brand} strokeWidth={2} fill="url(#equityFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardBody>
          </Card>
        </motion.div>

        <motion.div variants={item}>
          <Card className="h-full">
            <CardHeader title="Holdings allocation" description="Share of holdings value by instrument" />
            <CardBody>
              {allocation.length === 0 ? (
                <EmptyState icon={BarChart3} title="No holdings" description="Delivery holdings appear here with their weight and P&L." className="py-10" />
              ) : (
                <ul className="flex flex-col gap-3">
                  {allocation.map((a) => {
                    const w = Math.min(100, Math.max(0, toNum(a.weight_pct)));
                    return (
                      <li key={a.trading_symbol}>
                        <div className="flex items-baseline justify-between gap-2 text-xs">
                          <span className="min-w-0 truncate font-medium text-fg">{a.trading_symbol}</span>
                          <span className="shrink-0 font-tabular text-fg-subtle">
                            {formatINRCompact(a.value)} · {formatPctFixed(a.weight_pct).replace('+', '')}
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
                          <div className="h-full rounded-full bg-brand" style={{ width: `${w}%` }} />
                        </div>
                        <div className={cn('mt-0.5 text-[11px] font-tabular', signClass(a.pnl))}>{formatSignedINR(a.pnl)} overall</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardBody>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  );
}
